import { Link } from 'gatsby'
import React, { ReactNode, useContext, useEffect, useState } from 'react'

import { LocaleContext } from '../locale/i18n'
import { staticPagePaths } from '../locale/pathing'
import { getMetrics } from '../logic/metrics'
import publicConfig from '../logic/public-config'
import { FormFeedbackState } from '../logic/shared-types'
import { StaticPage } from '../logic/staticPage'

import Button from './Button'
import ContactDetailFields from './ContactDetailFields'
import { EmailLink } from './EmailLink'
import Heading from './Heading'
import IconCircledCheck from './icons/IconCircledCheck'
import IconInfo from './icons/IconInfo'
import Label from './Label'
import Textarea from './Textarea'

import './ContactForm.scss'

export const ContactFormSubmitted = () => {
  const { t } = useContext(LocaleContext)

  useEffect(() => {
    document
      .querySelector('.contactForm__formBox--success')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  return (
    <div className="contactForm__formBox contactForm__formBox--success">
      <IconCircledCheck width={64} height={64} />
      <h3 className="heading heading--md">
        {t('contactForm.success.heading')}
      </h3>
      <p>{t('contactForm.success.content')}</p>
    </div>
  )
}

const ContactForm = ({
  heading,
  instructions,
}: {
  heading?: string
  instructions?: ReactNode
}) => {
  const { locale, t, interpolate } = useContext(LocaleContext)
  const [message, setMessage] = useState('')
  const [feedbackState, setFeedbackState] = useState(FormFeedbackState.NONE)

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = Object.fromEntries([...formData.entries()]) as Record<
      string,
      string | number
    >

    setFeedbackState(FormFeedbackState.LOADING)

    const payload = {
      ...getMetrics(),
      pageLocale: locale,
      submissionType: 'customer',
      ...data,
    }
    fetch(`${String(publicConfig.apiHost)}/v1/contact-form/submit`, {
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(res => {
        if (res.status === 200) {
          setMessage('')
          setFeedbackState(FormFeedbackState.SUCCESS)
        } else {
          throw new Error(`Server returned HTTP ${res.status}`)
        }
      })
      .catch(() => setFeedbackState(FormFeedbackState.ERROR))
  }

  return (
    <div className="contactForm gutter--center">
      <Heading text={heading || t('contactForm.heading.primary')} />
      <div className="contactForm__lead">
        <p>
          {interpolate('contactForm.lead.primary', {
            b: s => <strong>{s}</strong>,
          })}
        </p>
      </div>
      {feedbackState === FormFeedbackState.SUCCESS ? (
        <ContactFormSubmitted />
      ) : (
        <div className="contactForm__formBox ga-contactForm">
          <form onSubmit={handleSubmit}>
            <fieldset className="contactForm__fields">
              <ContactDetailFields
                disabled={feedbackState === FormFeedbackState.LOADING}
              />
              <Label isRequired text={t('contactForm.label.message')}>
                <Textarea
                  onChange={setMessage}
                  id="message"
                  disabled={feedbackState === FormFeedbackState.LOADING}
                  name="message"
                  aria-label="message"
                  value={message}
                  required
                />
              </Label>
              <div className="contactFormInstructions">
                <div className="contactFormInstructions__icon">
                  <IconInfo width="32" height="32" />
                </div>
                <div className="richText contactFormInstructions__body">
                  {instructions ?? (
                    <>
                      <p>
                        <strong>
                          {t('contactForm.defaultInstructions.intro')}
                        </strong>
                      </p>
                      <ul className="richText__list richText__list--unordered">
                        <li className="richText__listItem">
                          {t('contactForm.defaultInstructions.1')}
                        </li>
                        <li className="richText__listItem">
                          {t('contactForm.defaultInstructions.2')}
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </fieldset>
            <Button
              className="justify-self-center"
              size="xl"
              isLoading={feedbackState === FormFeedbackState.LOADING}
              type="submit"
            >
              {t('contactForm.send')}
            </Button>
            {feedbackState === FormFeedbackState.ERROR && (
              <p
                className="contactForm__feedback contactForm__feedback--error"
                role="alert"
              >
                {t('contactForm.sendErr')}
              </p>
            )}
          </form>
        </div>
      )}
      {feedbackState !== FormFeedbackState.SUCCESS && (
        <p className="contactForm__alternates">
          {interpolate('contactForm.questions', {
            email: s => (
              <EmailLink
                email="info@measurlabs.com"
                location="contactFormSuccess"
              >
                {s}
              </EmailLink>
            ),
            link: s => (
              <Link
                to={`${staticPagePaths[StaticPage.CONTACT][locale]}#${t('contact.salesAnchor')}`}
              >
                {s}
              </Link>
            ),
          })}
        </p>
      )}
    </div>
  )
}

export default ContactForm
